import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCoins, FaWallet, FaUsers, FaCheckCircle, FaChartLine, FaClock, FaCopy, FaShareAlt, FaLink, FaHistory } from 'react-icons/fa';
import { AffiliateApiService } from '../../service/affiliateApiService';
import { useLoader } from '../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../base/context/MessageProvider';
import './affiliate.css';

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();

  useEffect(() => {
    loader.showLoading(true);
    AffiliateApiService.getDashboard()
      .then((res) => {
        loader.showLoading(false);
        if (res.status?.code === 200 && res.data) {
          setData(res.data);
        } else {
          setError(res.error?.message || 'Failed to load');
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        if (err?.status === 403 || err?.message?.toLowerCase?.().includes('affiliate')) {
          navigate('/', { replace: true });
          return;
        }
        setError(err?.message || 'Failed to load dashboard');
      });
  }, []);

  const copyLink = () => {
    if (!data?.referralLink) return;
    navigator.clipboard.writeText(data.referralLink).then(() => {
      message.showToast('Link copied to clipboard', ToastTypes.SUCCESS);
    });
  };

  const shareLink = () => {
    if (!data?.referralLink) return;
    if (navigator.share) {
      navigator.share({
        title: 'Join Importerr',
        text: 'Register as a seller using my referral link',
        url: data.referralLink,
      }).catch(() => {});
    } else {
      copyLink();
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–');
  const formatMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  if (error && !data) {
    return (
      <div className="affiliate-page">
        <p className="affiliate-error">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const eligibleThreshold = data.eligibleThreshold ?? 20000;
  const registrationBonus = data.registrationBonus ?? 500;
  const bonusDays = data.bonusDaysAfterDelivery ?? 15;

  const cards = [
    { label: 'Total Earnings', value: formatMoney(data.totalEarnings), icon: FaCoins },
    { label: 'IM Cash', value: formatMoney(data.walletBalance), icon: FaWallet },
    { label: 'Total Sellers', value: String(data.totalSellers || 0), icon: FaUsers },
    {
      label: 'Eligible Sellers',
      sub: `Crossed ₹${Number(eligibleThreshold).toLocaleString('en-IN')} threshold`,
      value: String(data.eligibleSellers || 0),
      icon: FaCheckCircle,
    },
    { label: 'This Month GMV', value: formatMoney(data.thisMonthGMV), icon: FaChartLine },
    { label: 'Pending Commission', value: formatMoney(data.pendingCommission), icon: FaClock },
  ];

  const recentActivity = data.recentActivity || [];
  const pendingCount = data.pendingSellersCount ?? 0;

  return (
    <div className="affiliate-page">
      <h1 className="affiliate-welcome">Welcome back, {data.welcomeName || 'Affiliate'}</h1>
      <p className="affiliate-subtitle">Track your referrals, commissions and performance in one place.</p>

      {pendingCount > 0 && (
        <div className="affiliate-callout">
          <strong>{pendingCount}</strong> referred seller(s) are not yet eligible. Only <strong>delivered</strong> order value counts. You earn ₹{Number(registrationBonus).toLocaleString('en-IN')} per seller when their delivered sales cross ₹{Number(eligibleThreshold).toLocaleString('en-IN')}, credited <strong>{bonusDays} days after</strong> they cross the threshold.
        </div>
      )}

      <div className="affiliate-cards">
        {cards.map((c) => (
          <div key={c.label} className="affiliate-card">
            <div className="affiliate-card-icon">{<c.icon size={20} />}</div>
            <div className="affiliate-card-body">
              <div className="affiliate-card-value">{c.value}</div>
              <div className="affiliate-card-label">{c.label}</div>
              {c.sub && <div className="affiliate-card-sub">{c.sub}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="affiliate-referral-section">
        <h2 className="affiliate-section-title">
          <FaLink size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
          Your referral link
        </h2>
        <p className="affiliate-section-desc">Share this link with sellers. You earn ₹{Number(registrationBonus).toLocaleString('en-IN')} per seller when their <strong>delivered</strong> orders cross ₹{Number(eligibleThreshold).toLocaleString('en-IN')}; the bonus is credited {bonusDays} days after they cross the threshold.</p>
        <div className="affiliate-referral-row">
          <input
            type="text"
            className="affiliate-referral-input"
            readOnly
            value={data.referralLink || ''}
          />
          <button type="button" className="affiliate-btn affiliate-btn-primary" onClick={copyLink}>
            <FaCopy /> Copy
          </button>
          <button type="button" className="affiliate-btn affiliate-btn-secondary" onClick={shareLink}>
            <FaShareAlt /> Share
          </button>
        </div>
        {data.referralCode && (
          <p className="affiliate-referral-code">Referral code: <strong>{data.referralCode}</strong></p>
        )}
      </div>

      <div className="affiliate-section">
        <h2 className="affiliate-section-title">
          <FaHistory size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
          Recent activity
        </h2>
        <p className="affiliate-section-desc">Earnings added to your IM Cash (not yet in bank). Withdraw when you’re ready.</p>
        {recentActivity.length === 0 ? (
          <p className="affiliate-empty">No commission activity yet.</p>
        ) : (
          <div className="affiliate-activity-list">
            {recentActivity.map((a, i) => (
              <div key={i} className="affiliate-activity-item">
                <div>
                  <div className="affiliate-activity-item-type">
                    {a.type === 'registration_bonus' ? 'Registration bonus' : 'Performance commission'}
                    {a.sellerName && a.sellerName !== '–' && ` · ${a.sellerName}`}
                  </div>
                  <div className="affiliate-activity-item-meta">{formatDate(a.createdAt)} · {a.status === 'credited' ? 'Progress' : a.status}</div>
                </div>
                <div className="affiliate-activity-item-amount">{formatMoney(a.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(data.sellerProgress && data.sellerProgress.length > 0) && (
        <div className="affiliate-referral-section">
          <h2 className="affiliate-section-title">Seller progress</h2>
          <p className="affiliate-section-desc">Delivered/shipped order value counts for eligibility. Bonus is credited {bonusDays} days after the seller’s delivered sales first cross ₹{Number(eligibleThreshold).toLocaleString('en-IN')}.</p>
          <div className="affiliate-table-wrap">
            <table className="affiliate-table">
              <thead>
                <tr>
                  <th>Seller</th>
                  <th>Email</th>
                  <th>Join date</th>
                  <th>Delivered sales</th>
                  <th>Progress to ₹{Number(eligibleThreshold).toLocaleString('en-IN')}</th>
                  <th>Eligible</th>
                </tr>
              </thead>
              <tbody>
                {data.sellerProgress.map((s) => (
                  <tr key={s.sellerId}>
                    <td>{s.name}</td>
                    <td>{s.email}</td>
                    <td>{s.joinDate ? new Date(s.joinDate).toLocaleDateString('en-IN') : '–'}</td>
                    <td>{formatMoney(s.totalSales)}</td>
                    <td>
                      <div className="affiliate-progress-cell">
                        <div className="affiliate-progress-bar-wrap">
                          <div
                            className="affiliate-progress-bar"
                            style={{ width: `${Math.min(100, s.progressPercent || 0)}%` }}
                          />
                          <span className="affiliate-progress-text">
                            {s.overThreshold > 0 ? `100% (₹${Number(s.overThreshold).toLocaleString('en-IN')} over)` : `${s.progressPercent || 0}%`}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`affiliate-badge ${s.eligible ? 'affiliate-badge-eligible' : 'affiliate-badge-pending'}`}>
                        {s.eligible ? 'Eligible' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="affiliate-referral-section">
        <h2 className="affiliate-section-title">Monthly earnings (commission)</h2>
        <p className="affiliate-section-desc">Credited commission over the last 6 months.</p>
        {data.monthlyEarningsChart && data.monthlyEarningsChart.length > 0 ? (
          <>
            <div className="affiliate-chart-wrap">
              <div className="affiliate-chart-bar">
                {data.monthlyEarningsChart.map((m) => {
                  const maxAmt = Math.max(1, ...data.monthlyEarningsChart.map((x) => x.amount || 0));
                  const pct = Math.max(8, (100 * (m.amount || 0)) / maxAmt);
                  return (
                    <div
                      key={m.month}
                      className="affiliate-chart-bar-item"
                      style={{ height: `${pct}%` }}
                      title={`${m.month}: ${formatMoney(m.amount)}`}
                    />
                  );
                })}
              </div>
              <div className="affiliate-chart-labels">
                {data.monthlyEarningsChart.map((m) => (
                  <span key={m.month} className="affiliate-chart-label-item">
                    {m.month}: <strong>{formatMoney(m.amount)}</strong>
                  </span>
                ))}
              </div>
            </div>
          </>
        ) : (
          <p className="affiliate-empty">No commission yet. When referred sellers’ <strong>delivered</strong> orders cross ₹{Number(eligibleThreshold).toLocaleString('en-IN')}, you earn ₹{Number(registrationBonus).toLocaleString('en-IN')} per seller (credited {bonusDays} days after they cross the threshold).</p>
        )}
      </div>
    </div>
  );
}
